import { Injectable, OnDestroy } from '@angular/core';
import { ReadspeakerElement } from '../models/readerspeaker-element';
import { CrudService } from './crud.service';
import { ReadspeakerElementFactory } from '../models/factories/readspeaker-element-factory';
import { environment } from '../../environments/environment';
import { ApplicationStateService } from './application-state.service';
import { RouteService } from './route.service';
import axios from 'axios';
import { CommunicationService } from './communication.service';
import { Subscription } from 'rxjs';
import { Loggable } from '../models/loggable';
import { PageService } from './page.service';
import { BookFeatureActivationService } from './book-feature-activation.service';

@Injectable({
    providedIn: 'root'
})
export class ReadspeakerService implements OnDestroy {

    private readspeakerElementsMap: Map<number, Array<ReadspeakerElement>> = new Map();
    private idToReadspeakerElementMap: Map<number, ReadspeakerElement> = new Map();
    private cancelTokens: Array<any> = [];
    private subscription: Subscription;

    constructor(private crudService: CrudService,
                private applicationStateService: ApplicationStateService,
                private communicationService: CommunicationService,
                private pageService: PageService,
                private bookFeatureActivationService: BookFeatureActivationService,
                private routeService: RouteService) {

        this.subscription = this.communicationService.pageTurnAnnounced$.subscribe(
            () => {
                this.cancelRequests();
            });

    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Get all readspeaker elements of a page.
     * Fetch if not already cached.
     *
     * @param {number} index
     * @returns {Promise<ReadspeakerElement[]>}
     */
    getReadspeakerElementsOfPage(index: number): Promise<Array<ReadspeakerElement>> {

        const factory = new ReadspeakerElementFactory();
        const entities = [];

        if (!this.applicationStateService.bookConfig.bookPermissions.pageTexts.view ||
            !this.applicationStateService.bookConfig.readSpeakerEnabled ||
            !this.bookFeatureActivationService.readspeakerElementsVisible ||
            this.applicationStateService.isOfflineModeEnabled
        ) {
            return new Promise(resolve => {
                resolve([]);
            });
        }

        return new Promise(resolve => {
            if (!this.pageService.getPageByIndex(index).readSpeakerEnabled) {
                resolve(undefined);

                return;
            }

            if (this.readspeakerElementsMap.has(index)) {
                resolve(this.readspeakerElementsMap.get(index));

                return;
            }

            const route = this.routeService.getAbsoluteRoute(environment.endpointUrls.readspeakerElements,
                {
                    bookId: this.applicationStateService.bookId,
                    index
                });

            const source = axios.CancelToken.source();
            this.cancelTokens.push(source);

            return axios.get(route, {
                withCredentials: true,
                cancelToken: source.token,
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then(result => {
                    result.data.forEach(element => {
                        const entity = factory.buildFromObject(element);
                        entities.push(entity);

                        this.idToReadspeakerElementMap.set(entity.id, entity);
                    });

                    this.readspeakerElementsMap.set(index, entities);

                    resolve(entities);
                }) as any;
        });
    }

    /**
     * Cancel outstanding request.
     */
    cancelRequests(): void {
        this.cancelTokens.forEach(ele => {
            if (ele) {
                ele.cancel();
            }
        });

        this.cancelTokens = [];
    }

    createReadspeakerElement(readspeakerElement: ReadspeakerElement): Promise<ReadspeakerElement> {
        this.crudService.getAllEntitiesOfBook(ReadspeakerElement, new ReadspeakerElementFactory());

        return this.crudService.create(readspeakerElement) as Promise<ReadspeakerElement>;
    }

    updateReadspeakerElement(readspeakerElement: ReadspeakerElement): Promise<ReadspeakerElement> {
        return this.crudService.update(readspeakerElement) as Promise<ReadspeakerElement>;
    }

    deleteReadspeakerElement(readspeakerElement: ReadspeakerElement): Promise<boolean> {
        return this.crudService.remove(readspeakerElement) as Promise<boolean>;
    }

    getReadProperties(readspeakerElement: ReadspeakerElement): { url: string; eleId: string; language: string } {

        const thiz = this;

        let absoluteURL = thiz.routeService.getAbsoluteRoute(environment.endpointUrls.readspeakerTextUrl,
            {
                bookId: thiz.applicationStateService.bookId,
                id: readspeakerElement.id
            });

        absoluteURL = absoluteURL.replace('mein.lemberger.local', 'lemberger.staging.sofamedia.at');
        absoluteURL = absoluteURL.replace('digi.schule.local', 'lemberger.staging.sofamedia.at');
        absoluteURL = encodeURIComponent(absoluteURL);

        const id = `read-${readspeakerElement.id}`;

        if (!this.applicationStateService.bookConfig.readSpeakerLanguage) {
            this.applicationStateService.bookConfig.readSpeakerLanguage = 'de_de';
        }

        return { url: absoluteURL, eleId: id, language: this.applicationStateService.bookConfig.readSpeakerLanguage };
    }

    logUsage(readspeakerButton: Loggable): Promise<boolean> {
        return this.crudService.log(readspeakerButton);
    }

}
